import { useDeleteVacancyCompanyCandidateMutation } from "@generated/graphql";
import { Button, Grid, IconButton, Stack, Typography } from "@mui/material";
import DeleteIcon from "assets/icons/DeleteIcon";
import ROUTES from "constants/routes";
import { ConfirmModalContext } from "context/confirmModalContext";
import { ToastContext, ToastTypeEnum } from "context/toastContext";
import { useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DownloadIcon from "assets/icons/DownloadIcon";
import { parseApolloError } from "helpers/errorsParser";
import qs from "qs";
import { useCandidateVacanciesQuery } from "../../../../@generated/graphql";

interface VacanciesListProps {
  isEditing: boolean;
}

const VacanciesList = ({ isEditing }: VacanciesListProps) => {
  const { candidateId } = useParams();
  const navigate = useNavigate();
  const { addToast } = useContext(ToastContext);
  const { handleOpen } = useContext(ConfirmModalContext);
  const { data, refetch } = useCandidateVacanciesQuery({ variables: { candidateId: candidateId! } });
  const [deleteVacancyCompanyCandidate] = useDeleteVacancyCompanyCandidateMutation();

  const vacancyCompanyCandidates = data?.companyCandidate?.vacancyCompanyCandidates;

  const handleDeleteVacancyCompanyCandidate = (id: string) => {
    deleteVacancyCompanyCandidate({ variables: { vacancyCompanyCandidateId: id } })
      .then(() => {
        addToast({ type: ToastTypeEnum.SUCCESS });
        refetch();
      })
      .catch((e) => parseApolloError(e, { addToast }));
  };

  const onDeleteVacancy = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();

    handleOpen({
      title: "Вы уверены, что хотите удалить вакансию?",
      actionButtonText: "Удалить",
      handleClick: () => handleDeleteVacancyCompanyCandidate(id),
    });
  };

  const navigateToVacancy = (id: string) => {
    navigate({ pathname: `/${ROUTES.VACANCIES}/view/${id}`, search: qs.stringify({ candidateId }) });
  };

  const chooseCvCheckColor = (score: number) => {
    if (score >= 0 && score <= 39) {
      return "red.contrast";
    }
    if (score >= 40 && score <= 59) {
      return "logo";
    }

    return "green.contrast";
  };

  if (!vacancyCompanyCandidates?.length) return "";

  return (
    <Grid
      item
      xs={12}
      md={12}>
      <Typography
        variant='h20'
        sx={{ marginBottom: 1.5 }}>
        Вакансии
      </Typography>
      <Stack
        direction='row'
        spacing={1.5}
        flexWrap='wrap'
        useFlexGap>
        {vacancyCompanyCandidates?.map((vacancyCompanyCandidate) => {
          const currentCvCheckScore = vacancyCompanyCandidate.currentCvCheck?.score;
          const reportFileUrl = vacancyCompanyCandidate.currentCvCheck?.reportFile?.url;

          return (
            <Button
              key={`vacancyCompanyCandidate-${vacancyCompanyCandidate.id}-${vacancyCompanyCandidate.vacancy.id}`}
              variant='outlined'
              size='small'
              component='div'
              onClick={() => navigateToVacancy(vacancyCompanyCandidate.vacancy.id)}
              sx={{ gap: 1, paddingRight: 0 }}>
              <Typography sx={{ fontWeight: 700 }}>{vacancyCompanyCandidate.vacancy.title}.</Typography>
              {Boolean(currentCvCheckScore) && (
                <Typography>
                  Соответствие вакансии:{" "}
                  <Typography
                    component='span'
                    sx={{ color: chooseCvCheckColor(currentCvCheckScore!) }}>
                    {currentCvCheckScore}
                  </Typography>
                </Typography>
              )}
              {Boolean(reportFileUrl) && (
                <IconButton
                  href={reportFileUrl!}
                  title='Отчет по резюме'
                  onClick={(e) => e.stopPropagation()}
                  target='_blank'
                  sx={{ marginRight: -1 }}>
                  <DownloadIcon />
                </IconButton>
              )}
              <IconButton
                onClick={(e) => onDeleteVacancy(e, vacancyCompanyCandidate.id)}
                disabled={!isEditing}>
                <DeleteIcon />
              </IconButton>
            </Button>
          );
        })}
      </Stack>
    </Grid>
  );
};

export default VacanciesList;
